// Audit log data hooks. Read-only; all four routes require admin role
// (admin-bff enforces).
import { useMutation, useQuery } from "@tanstack/react-query";

import { api } from "./api";

export interface AuditEvent {
    id: number;
    ts: string;
    actor_user_id?: string;
    actor_email?: string;
    actor_kind: "user" | "system" | "api_key" | string;
    action: string;
    resource_type: string;
    resource_id: string;
    tenant_id?: string;
    tenant_slug?: string;
    ip?: string;
    user_agent?: string;
    payload: Record<string, unknown>;
    prev_hash?: string | number[];
    hash?: string | number[];
    verified?: boolean;
}

export interface AuditPage {
    rows: AuditEvent[];
    next_id?: number;
    total?: number;
}

export interface AuditVerifyResult {
    ok: boolean;
    scanned: number;
    from_id: number;
    to_id: number;
    first_mismatch_id?: number;
    first_mismatch_expected?: string;
    first_mismatch_actual?: string;
}

export interface AuditFilters {
    q?: string;
    actor_kind?: string;
    action?: string; // prefix
    resource_type?: string;
    from_ts?: string;
    to_ts?: string;
    before_id?: number;
    limit?: number;
}

function toQuery(f: AuditFilters): string {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) {
        if (v !== undefined && v !== null && v !== "") {
            params.set(k, String(v));
        }
    }
    const s = params.toString();
    return s ? `?${s}` : "";
}

const AUDIT_KEY = ["admin", "audit"] as const;

export function useAuditList(filters: AuditFilters) {
    return useQuery({
        queryKey: [...AUDIT_KEY, filters],
        queryFn: () => api.get<AuditPage>(`/api/v2/audit${toQuery(filters)}`),
        staleTime: 5_000,
    });
}

export function useAuditEvent(id: number | null) {
    return useQuery({
        queryKey: [...AUDIT_KEY, "event", id],
        queryFn: () => api.get<AuditEvent>(`/api/v2/audit/${id}`),
        enabled: id !== null,
    });
}

export function useAuditVerify() {
    return useMutation({
        mutationFn: (bounds: { fromId?: number; toId?: number } = {}) =>
            api.post<AuditVerifyResult>("/api/v2/audit/verify", bounds),
    });
}

/**
 * Trigger a direct browser download of the full NDJSON export, filtered by
 * the currently-applied filters. Uses a Blob so the file lands as a proper
 * attachment instead of streaming into the current tab.
 */
export async function exportAuditNDJSON(filters: AuditFilters): Promise<void> {
    const res = await fetch(`/api/v2/audit/export.ndjson${toQuery(filters)}`, {
        credentials: "include",
        headers: { Accept: "application/x-ndjson" },
    });
    if (!res.ok) {
        throw new Error(`export failed: HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-${new Date().toISOString().replace(/[:.]/g, "-")}.ndjson`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}
